import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Menu as MenuIcon, LayoutGrid, Soup, CookingPot, Drumstick, CupSoda, Plus, UtensilsCrossed, Cake, Coffee, Fish, Salad, Pizza, Sandwich, IceCreamCone, WifiOff } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { api, rm, cachedMenu, fetchMenu } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ClosedNotice } from "@/components/customer/ClosedNotice";

import { useBranding, mediaSrc, HERO_DEFAULT, LOGO_DEFAULT } from "@/lib/branding";

const ICONS = [
  [/nasi|rice/i, Soup], [/^mi$|mee|mi |kuey|noodle|bihun/i, CookingPot], [/ayam|chicken|daging|meat/i, Drumstick],
  [/minum|drink|air|jus|juice/i, CupSoda], [/kopi|coffee|teh|tea/i, Coffee], [/kek|cake|dessert|pencuci/i, Cake],
  [/ikan|fish|seafood|laut/i, Fish], [/sayur|salad|vege/i, Salad], [/pizza/i, Pizza], [/burger|roti|sandwich|bread/i, Sandwich],
  [/ais krim|ice cream|aiskrim/i, IceCreamCone],
];
const catIcon = (name) => ICONS.find(([re]) => re.test(name))?.[1] || UtensilsCrossed;
const PLACEHOLDER = "/img/logo.png";

function Hero({ q, setQ }) {
  const b = useBranding();
  return (
    <header className="relative h-[300px] rounded-b-[28px] overflow-hidden bg-[#1a1208]" data-testid="home-hero">
      <img src={mediaSrc(b.hero_image) || HERO_DEFAULT} alt="" data-testid="home-hero-image" className="absolute right-[-30%] top-0 h-full w-[95%] object-cover opacity-90" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,#140d05_35%,rgba(20,13,5,0.55)_65%,rgba(20,13,5,0.1))]" />
      <div className="relative px-5 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-12 h-12 rounded-xl bg-white p-1 shadow-lg"><img src={mediaSrc(b.logo_image) || LOGO_DEFAULT} alt={b.shop_name} data-testid="home-logo" className="w-full h-full object-contain" /></div>
          <div className="text-white leading-none">
            <div className="font-display font-extrabold text-xl tracking-wide" data-testid="home-shop-name">{b.shop_name}</div>
            <div className="font-script text-sm text-amber-400" data-testid="home-shop-tagline">{b.shop_tagline}</div>
          </div>
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="home-menu-button" className="text-white p-2 rounded-full hover:bg-white/10 transition-colors"><MenuIcon className="w-6 h-6" /></button>
          </SheetTrigger>
          <SheetContent side="right" className="w-64">
            <SheetTitle className="font-display">POKYA RESTAURANT</SheetTitle>
            <div className="mt-6 flex flex-col gap-3 text-sm">
              <Link to="/akaun" data-testid="drawer-akaun-link" className="py-2 border-b">Akaun Saya</Link>
              <Link to="/troli" data-testid="drawer-troli-link" className="py-2 border-b">Troli Pesanan</Link>
              <Link to="/panel" data-testid="drawer-staff-link" className="py-2 border-b text-[#B91C1C] font-semibold">Panel Pekerja</Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="relative px-5 mt-8 text-white rise">
        <p className="font-display text-xl font-bold" data-testid="home-welcome">{b.welcome_text}</p>
        <h1 className="font-display text-[34px] leading-tight font-extrabold" data-testid="home-title">{b.hero_title}</h1>
        <p className="text-sm text-white/85 mt-2 max-w-[60%]" data-testid="home-subtitle">{b.hero_subtitle}</p>
      </div>
      <div className="absolute left-5 right-5 bottom-5">
        <div className="flex items-center gap-2 bg-white rounded-full px-4 h-11 shadow-md">
          <Search className="w-4 h-4 text-slate-500" />
          <input data-testid="menu-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={b.search_placeholder}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400" />
        </div>
      </div>
    </header>
  );
}

function MenuCard({ m, i }) {
  const nav = useNavigate();
  const { add } = useCart();
  const quickAdd = (e) => {
    e.stopPropagation();
    if (m.options?.length) return nav(`/menu/${m.id}`);
    add({ menu_id: m.id, name: m.name, image: m.image, option: m.options?.[0]?.name || "", unit_price: m.price + (m.options?.[0]?.price_delta || 0), qty: 1, note: "" });
  };
  return (
    <motion.div layout onClick={() => nav(`/menu/${m.id}`)} data-testid={`menu-card-${m.id}`}
      initial={{ opacity: 0, y: 18, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] } }}
      exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.15 } }} whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="overflow-hidden"><img src={m.image || PLACEHOLDER} alt={m.name} className={`w-full h-28 group-hover:scale-110 transition-transform duration-500 ${m.image ? "object-cover" : "object-contain p-4 bg-amber-50"}`} loading="lazy" /></div>
      <div className="p-3">
        <h3 className="font-display font-bold text-[14px] leading-snug min-h-[38px]">{m.name}</h3>
        <div className="flex items-center justify-between mt-1">
          <span className="font-bold text-sm">{rm(m.price)}</span>
          <button onClick={quickAdd} data-testid={`quick-add-${m.id}`} aria-label="Tambah"
            className="w-8 h-8 rounded-full bg-[#B91C1C] text-white flex items-center justify-center active:scale-90 transition-transform">
            <Plus className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const [menu, setMenu] = useState(cachedMenu);
  const [failed, setFailed] = useState(false);
  const [cat, setCat] = useState("Semua");
  const [q, setQ] = useState("");
  const [shop, setShop] = useState(null);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  useEffect(() => {
    const load = () => fetchMenu().then((m) => { setMenu(m); setFailed(false); setOffline(false); }).catch(() => { setFailed(true); setOffline(true); });
    const goOff = () => setOffline(true);
    load();
    window.addEventListener("online", load);
    window.addEventListener("offline", goOff);
    return () => { window.removeEventListener("online", load); window.removeEventListener("offline", goOff); };
  }, []);
  useEffect(() => { api.get("/public/settings").then((r) => setShop(r.data)).catch(() => {}); }, []);
  const cats = useMemo(() => [{ key: "Semua", icon: LayoutGrid }, ...[...new Set(menu.map((m) => m.category))].map((key) => ({ key, icon: catIcon(key) }))], [menu]);
  const list = useMemo(() => {
    const byCat = cat === "Semua" ? menu : menu.filter((m) => m.category === cat);
    return q ? byCat.filter((m) => m.name.toLowerCase().includes(q.toLowerCase())) : byCat;
  }, [menu, cat, q]);

  return (
    <div>
      <Hero q={q} setQ={setQ} />
      {offline && menu.length > 0 && (
        <div className="mx-5 mt-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="offline-notice">
          <WifiOff className="w-4 h-4 shrink-0" />Tiada internet – memaparkan menu tersimpan
        </div>
      )}
      {shop && !shop.is_open && <div className="px-5"><ClosedNotice shop={shop} className="mt-5" /></div>}
      <div className="flex gap-3 px-5 mt-5 overflow-x-auto no-scrollbar" data-testid="category-list">
        {cats.map(({ key, icon: Icon }) => {
          const active = cat === key;
          return (
            <button key={key} onClick={() => setCat(key)} data-testid={`category-${key.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative shrink-0 min-w-[60px] px-2 h-[68px] rounded-2xl flex flex-col items-center justify-center gap-1.5 text-[11px] font-medium transition-colors active:scale-95 ${active ? "text-white" : "text-slate-700 hover:bg-amber-50"}`}>
              {active && <motion.span layoutId="cat-pill" className="absolute inset-0 rounded-2xl bg-[#B91C1C] shadow-md" transition={{ type: "spring", stiffness: 420, damping: 32 }} />}
              <Icon className={`relative w-7 h-7 ${active ? "text-white" : "text-amber-500"}`} strokeWidth={1.8} />
              <span className="relative whitespace-nowrap">{key}</span>
            </button>
          );
        })}
      </div>
      <h2 className="font-display font-bold text-xl px-5 mt-6 mb-3">{q ? "Hasil Carian" : cat === "Semua" ? "Menu Pilihan" : cat}</h2>
      <div className="grid grid-cols-2 gap-4 px-5" data-testid="menu-grid">
        <AnimatePresence mode="popLayout">
          {list.map((m, i) => <MenuCard key={m.id} m={m} i={i} />)}
        </AnimatePresence>
      </div>
      {failed && !menu.length && <p className="px-5 text-sm text-red-600" data-testid="menu-error">Gagal memuatkan menu. Sila muat semula.</p>}
      {!failed && list.length === 0 && <p className="px-5 text-sm text-slate-500" data-testid="menu-empty">Tiada menu dijumpai.</p>}
    </div>
  );
}
